import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoModal,
  RedoModalButtonProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";

interface SupportV2ViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
  reservedViewNames: Set<string>;
  currentName?: string;
  mode: "create" | "edit";
}

export const SupportV2ViewModal = memo(function SupportV2ViewModal({
  isOpen,
  onClose,
  onSubmit,
  reservedViewNames,
  currentName = "",
  mode,
}: SupportV2ViewModalProps) {
  const [viewName, setViewName] = useState(currentName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [makeViewPrivate, setMakeViewPrivate] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(viewName.trim());
      onClose();
    } finally {
      setIsSubmitting(false);
      if (mode === "create") {
        setViewName("");
      }
    }
  };

  const isNameReserved =
    reservedViewNames.has(viewName.trim().toLowerCase()) &&
    (mode === "create" ||
      viewName.trim().toLowerCase() !== currentName.toLowerCase());

  const primaryButton: RedoModalButtonProps = {
    text: mode === "create" ? "Add" : "Save",
    onClick: handleSubmit,
    disabled: !viewName.trim().length || isNameReserved || isSubmitting,
    pending: isSubmitting,
    size: "lg",
  };

  const secondaryButton: RedoModalButtonProps = {
    text: "Cancel",
    onClick: onClose,
    disabled: isSubmitting,
    size: "lg",
  };

  return (
    <RedoModal
      isOpen={isOpen}
      onModalCloseRequested={isSubmitting ? () => {} : onClose}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      title={mode === "create" ? "Add new view" : "Edit view"}
    >
      <Flex dir="column" gap="xl">
        <Flex dir="column">
          <Text>Name</Text>
          <RedoTextInput
            setValue={setViewName}
            size="sm"
            state={isSubmitting ? "readonly" : "default"}
            value={viewName}
          />
        </Flex>
        {mode === "create" && (
          <RedoCheckbox
            label="Only visible to me"
            setValue={(val) => setMakeViewPrivate(!!val)}
            value={makeViewPrivate}
          />
        )}
      </Flex>
    </RedoModal>
  );
});
